import { BuilderView, WorkflowTypes } from './types';
import { CanvasWorkflowBuilderState } from 'src/slices/canvas-workflow-builder/workflowBuilderSlice';
import { TranslationChannel } from 'src/types/workflowBuilderAPITypes';
import {
  ActionBuilderActionTypes,
  FlamethrowerTrackingEventTypes,
} from 'src/utils/enums';

export enum CanvasModes {
  ACTION = 'action',
  ACTION_EDIT = 'action-edit',
  ARTICLE_PICKER = 'article-picker',
  ATTACHMENT_ANALYSIS_UPLOAD = 'attachment-analysis-upload',
  BUTTON = 'button',
  CODE_BLOCK = 'code-block',
  CONDITION = 'condition',
  CSAT_TRIGGER_POINT = 'csat-trigger-point',
  CUSTOM_HANDOFF = 'custom-handoff',
  EMBED = 'embed',
  FORM = 'form',
  GO_TO_COMPONENT = 'go-to-component',
  GO_TO_INTENT = 'go-to-intent',
  IMAGE = 'image',
  MESSAGE = 'message',
  MINI_AUTOFLOW_POLICY = 'mini-autoflow-policy',
  SPEECH_STEP = 'speech-step',
}

export const actionToTrackingEventMap: Record<
  string,
  FlamethrowerTrackingEventTypes
> = {
  [ActionBuilderActionTypes.ZENDESK_HANDOFF]:
    FlamethrowerTrackingEventTypes.WORKFLOW_ZENDESK_HANDOFF_ACTION_ADDED,
  [ActionBuilderActionTypes.ZENDESK_CHAT_HANDOFF_V2]:
    FlamethrowerTrackingEventTypes.WORKFLOW_ZENDESK_CHAT_HANDOFF_ACTION_ADDED,
  [ActionBuilderActionTypes.ZENDESK_TICKET_CREATION]:
    FlamethrowerTrackingEventTypes.WORKFLOW_ZENDESK_TICKET_CREATION_ACTION_ADDED,
  [ActionBuilderActionTypes.INTERCOM_TICKET_CREATION]:
    FlamethrowerTrackingEventTypes.WORKFLOW_INTERCOM_TICKET_CREATION_ACTION_ADDED,
  [ActionBuilderActionTypes.FRONT_TICKET_CREATION]:
    FlamethrowerTrackingEventTypes.WORKFLOW_FRONT_TICKET_CREATION_ACTION_ADDED,
  [ActionBuilderActionTypes.HALOCRM_TICKET_CREATION]:
    FlamethrowerTrackingEventTypes.WORKFLOW_HALOCRM_TICKET_CREATION_ACTION_ADDED,
  [ActionBuilderActionTypes.ZAMMAD_TICKET_CREATION]:
    FlamethrowerTrackingEventTypes.WORKFLOW_ZAMMAD_TICKET_CREATION_ACTION_ADDED,
  [ActionBuilderActionTypes.PYLON_TICKET_CREATION]:
    FlamethrowerTrackingEventTypes.WORKFLOW_PYLON_TICKET_CREATION_ACTION_ADDED,
  [ActionBuilderActionTypes.ARTICLE_SUGGESTION]:
    FlamethrowerTrackingEventTypes.WORKFLOW_ADDED_ARTICLE_SUGGESTION,
  [ActionBuilderActionTypes.SALESFORCE_HANDOFF]:
    FlamethrowerTrackingEventTypes.WORKFLOW_SALESFORCE_HANDOFF_ACTION_ADDED,
  [ActionBuilderActionTypes.SALESFORCE_CASE_CREATION]:
    FlamethrowerTrackingEventTypes.WORKFLOW_SALESFORCE_CASE_CREATION_ACTION_ADDED,
  [ActionBuilderActionTypes.SALESFORCE_CHAT_HANDOFF]:
    FlamethrowerTrackingEventTypes.WORKFLOW_SALESFORCE_CHAT_HANDOFF_ACTION_ADDED,
  [ActionBuilderActionTypes.CATCH_ALL_HANDOFF]:
    FlamethrowerTrackingEventTypes.WORKFLOW_GENERIC_HANDOFF_ACTION_ADDED,
  [ActionBuilderActionTypes.JIRA_SERVICE_MANAGEMENT_CREATE_REQUEST]:
    FlamethrowerTrackingEventTypes.WORKFLOW_JIRA_HANDOFF_ACTION_ADDED,
  [ActionBuilderActionTypes.KUSTOMER_CHAT_HANDOFF]:
    FlamethrowerTrackingEventTypes.WORKFLOW_KUSTOMER_CHAT_HANDOFF_ACTION_ADDED,
  [ActionBuilderActionTypes.KUSTOMER_CONVERSATION_CREATION]:
    FlamethrowerTrackingEventTypes.WORKFLOW_KUSTOMER_CONVERSATION_CREATION_ACTION_ADDED,
  [ActionBuilderActionTypes.CUSTOM_EVENT]:
    FlamethrowerTrackingEventTypes.WORKFLOW_CUSTOM_EVENT_ACTION_ADDED,
  [ActionBuilderActionTypes.DYNAMICS_365_CASE_CREATION]:
    FlamethrowerTrackingEventTypes.WORKFLOW_DYNAMICS_365_CASE_CREATION_ACTION_ADDED,
  [ActionBuilderActionTypes.SERVICENOW_CASE_CREATION]:
    FlamethrowerTrackingEventTypes.WORKFLOW_SERVICENOW_CASE_CREATION_ACTION_ADDED,
  [ActionBuilderActionTypes.SERVICENOW_CASE_INCIDENT_CREATION]:
    FlamethrowerTrackingEventTypes.WORKFLOW_SERVICENOW_CASE_INCIDENT_CREATION_ACTION_ADDED,
};

//Builder page nav constants
export const WIDGET = 'widget';
const EMAIL = 'email';
export const API = 'api';

export const tabs: Array<{
  beta?: boolean;
  label: string;
  view: BuilderView;
}> = [
  { label: 'Widget', view: WIDGET },
  { label: 'Email', view: EMAIL },
  { label: 'Voice', view: 'voice' },
  { label: 'Web Call', view: 'web_call' },
  { label: 'Headless', view: API },
  { label: 'Slack', view: 'slack' },
  { label: 'Assist', view: 'playbook' },
];

export const widgetModes = [
  {
    label: 'Autoflows',
    value: 'autoflow',
  },
  { label: 'Classic', value: 'classic' },
] as const;
export type WidgetMode = (typeof widgetModes)[number]['value'];

export const widgetWorkflowTypes: Array<WorkflowTypes> = [
  'flamethrower-preview',
  'flamethrower-live',
];

export const interactiveEmailWorkflowTypes: WorkflowTypes[] = [
  'interactive-email-live',
  'interactive-email-preview',
];

export const emailWorkflowTypes: Array<WorkflowTypes> = ['email'];

export const apiWorkflowTypes: Array<WorkflowTypes> = [
  'api-live',
  'api-preview',
];

export const slackWorkflowTypes: Array<WorkflowTypes> = [
  'slack-live',
  'slack-preview',
];

export const voiceWorkflowTypes: Array<WorkflowTypes> = [
  'voice-live',
  'voice-preview',
];

export const webCallWorkflowTypes: Array<WorkflowTypes> = [
  'web-call-live',
  'web-call-preview',
];

export const playbookWorkflowTypes: Array<WorkflowTypes> = [
  'playbook-live',
  'playbook-preview',
];

export const viewToWorkflowTypes: Record<
  BuilderView | 'interactive_email',
  WorkflowTypes[]
> = {
  api: apiWorkflowTypes,
  email: emailWorkflowTypes,
  interactive_email: interactiveEmailWorkflowTypes,
  playbook: playbookWorkflowTypes,
  slack: slackWorkflowTypes,
  voice: voiceWorkflowTypes,
  web_call: webCallWorkflowTypes,
  widget: widgetWorkflowTypes,
};

export const channelToWorkflowTypes: Record<
  TranslationChannel,
  WorkflowTypes[]
> = {
  api: ['api-live'],
  email: ['email'],
  interactive_email: ['interactive-email-live'],
  playbook: ['playbook-live'],
  slack: ['slack-live'],
  voice: ['voice-live'],
  web_call: ['web-call-live'],
  widget: ['flamethrower-live'],
};

//API error messages

export const emailConfigurationSubVersionConflictException =
  'Can only update latest draft Email Configuration version';

export const staleRecordException = 'Cannot update stale record';

export const staleEmailWorkflowException =
  'Can only update latest email workflow version';

export const SESSION_COPY_PASTE_STORAGE_KEY = 'copy-paste';

export const CIRCULAR_PATH_MESSAGE = 'Circular path found between steps';

// Multilingual Error Types
export const STALE_ENABLED_LANGUAGES_CONFIGURATION =
  'STALE_ENABLED_LANGUAGES_CONFIGURATION';

export const STALE_WIDGET_TRANSLATION = 'STALE_WIDGET_TRANSLATION';

export const STALE_EMAIL_TRANSLATION = 'STALE_EMAIL_TRANSLATION';

export const TEMPLATE_INTENT_ID = 'template';
export const TEMPLATE_INTENT_WORKFLOW_ID_PREFIX = `${TEMPLATE_INTENT_ID}_`;
export const TEMPLATE_CLONE_TOAST_OPTIONS = {
  subtitle:
    'Replace the template elements with your specific business logic, such as Actions and Handoffs, to align it with your needs.',
  title: 'Customize and modify the template to apply your business logic.',
  variant: 'warning',
} as const;

// API error types
export const EMPTY_WORKFLOW = 'EMPTY_WORKFLOW';
export const NO_LIVE_VERSION_IS_PUBLISHED = 'NO_LIVE_VERSION_IS_PUBLISHED';
export const EMAIL_WORKFLOW_NOT_CREATED = 'EMAIL_WORKFLOW_NOT_CREATED';
export const API_WORKFLOW_NOT_FOUND = 'API_WORKFLOW_NOT_FOUND';

export const routeFilterOptions: {
  label: CanvasWorkflowBuilderState['routeFilter'];
  value: CanvasWorkflowBuilderState['routeFilter'];
}[] = [
  {
    label: 'All',
    value: 'All',
  },
  {
    label: 'Subagent - Classic',
    value: 'Subagent - Classic',
  },
  {
    label: 'Subagent - Autoflows',
    value: 'Subagent - Autoflows',
  },
  {
    label: 'Agent - Classic',
    value: 'Agent - Classic',
  },
  {
    label: 'Agent - Autoflows',
    value: 'Agent - Autoflows',
  },
];

const CLASSIC_ROUTE_FILTER_VALUES: CanvasWorkflowBuilderState['routeFilter'][] =
  ['Subagent - Classic', 'Agent - Classic'];

/**
 * Filters out the classic options from the route filter dropdown when
 * classic_workflows_deprecated is on.
 */
export const getVisibleRouteFilterOptions = (
  isClassicWorkflowsDeprecated: boolean,
) =>
  isClassicWorkflowsDeprecated
    ? routeFilterOptions.filter(
        opt => !CLASSIC_ROUTE_FILTER_VALUES.includes(opt.value),
      )
    : routeFilterOptions;

/**
 * Reconciles a persisted routeFilter value when the flag flips on — a stale
 * 'Agent - Classic' / 'Subagent - Classic' selection becomes 'All'.
 */
export const normalizeRouteFilter = (
  routeFilter: CanvasWorkflowBuilderState['routeFilter'],
  isClassicWorkflowsDeprecated: boolean,
): CanvasWorkflowBuilderState['routeFilter'] =>
  isClassicWorkflowsDeprecated &&
  CLASSIC_ROUTE_FILTER_VALUES.includes(routeFilter)
    ? 'All'
    : routeFilter;

export const actionFilterOptions: {
  label: CanvasWorkflowBuilderState['actionFilter'];
  value: CanvasWorkflowBuilderState['actionFilter'];
}[] = [
  {
    label: 'All',
    value: 'All',
  },
  {
    label: 'API',
    value: 'API',
  },
  { label: 'Recommended', value: 'Recommended' },
  {
    label: 'Others',
    value: 'Others',
  },
  {
    label: 'MCP',
    value: 'MCP',
  },
  {
    label: 'Browser Agent',
    value: 'Browser Agent',
  },
];

/**
 * Action types hidden from the component-scoped Actions lists — `ActionTabContent`
 * (classic builder), `AgentActionTabContent` (agent builder), and the email
 * builder's `ActionList`.
 *
 * Deliberately NOT applied to the Global tools drawer (`ActionDrawer`): MCP and
 * browser agent are perfectly valid as global agent tools and inline autoflow
 * policy references. They are excluded here because dropping one into a component
 * posts to `/workflow-builder/{id}/{version}/action`, which compiles the action
 * into classic canvas steps — and neither has an entry in the backend's
 * `STEP_TYPE_TO_CLASS`, so that request always fails. Listing them here keeps a
 * guaranteed-failing action out of the picker; the backend rejects the add with
 * an explicit message (`is_classic_canvas_supported`).
 */
export const disabledActionTypes: string[] = [
  ActionBuilderActionTypes.CODE_EXECUTION,
  ActionBuilderActionTypes.DYNAMIC_CARD,
  ActionBuilderActionTypes.PRODUCT_RECOMMENDATION,
  ActionBuilderActionTypes.SNOWFLAKE_DATA_QUERY_TEMPLATE,
  ActionBuilderActionTypes.CUSTOM_EVENT,
  ActionBuilderActionTypes.MCP,
  ActionBuilderActionTypes.BROWSER_AGENT,
];

export const customizableActions = [
  ActionBuilderActionTypes.ZENDESK_HANDOFF,
  ActionBuilderActionTypes.SALESFORCE_HANDOFF,
  ActionBuilderActionTypes.SALESFORCE_CASE_CREATION,
  ActionBuilderActionTypes.SALESFORCE_CHAT_HANDOFF,
  ActionBuilderActionTypes.ARTICLE_SUGGESTION,
  ActionBuilderActionTypes.CATCH_ALL_HANDOFF,
  ActionBuilderActionTypes.JIRA_SERVICE_MANAGEMENT_CREATE_REQUEST,
  ActionBuilderActionTypes.INTERCOM_HANDOFF,
  ActionBuilderActionTypes.ZENDESK_CHAT_HANDOFF,
  ActionBuilderActionTypes.ZENDESK_CHAT_HANDOFF_V2,
  ActionBuilderActionTypes.FRONT_TICKET_CREATION,
  ActionBuilderActionTypes.HALOCRM_TICKET_CREATION,
  ActionBuilderActionTypes.ZAMMAD_TICKET_CREATION,
  ActionBuilderActionTypes.ZENDESK_TICKET_CREATION,
  ActionBuilderActionTypes.HYPERLINK_REDIRECT,
  ActionBuilderActionTypes.KUSTOMER_HANDOFF,
  ActionBuilderActionTypes.KUSTOMER_CHAT_HANDOFF,
  ActionBuilderActionTypes.KUSTOMER_CONVERSATION_CREATION,
  ActionBuilderActionTypes.TRIGGER_EVENT,
  ActionBuilderActionTypes.FRESHDESK_TICKET_CREATION,
  ActionBuilderActionTypes.FRESHDESK_TICKET_AND_CONTACT_CREATION,
  ActionBuilderActionTypes.ARTICLE_SUGGESTION_GRANULAR,
  ActionBuilderActionTypes.SNAPENGAGE_CHAT_HANDOFF,
  ActionBuilderActionTypes.GORGIAS_CHAT_HANDOFF,
  ActionBuilderActionTypes.GORGIAS_TICKET_CREATION,
  ActionBuilderActionTypes.PYLON_TICKET_CREATION,
  ActionBuilderActionTypes.TALKDESK_CHAT_HANDOFF,
  ActionBuilderActionTypes.GRANULAR_ARTICLE_SUGGESTION_V2,
  ActionBuilderActionTypes.FRESHCHAT_CHAT_HANDOFF,
  ActionBuilderActionTypes.ZENDESK_MESSAGING_HANDOFF,
  ActionBuilderActionTypes.SALESFORCE_MESSAGING_HANDOFF,
  ActionBuilderActionTypes.CONTEXT_VARIABLE_MAPPING,
  ActionBuilderActionTypes.ZENDESK_ATTACHMENT_UPLOAD,
  ActionBuilderActionTypes.LIVE_CHAT_CHAT_HANDOFF,
  ActionBuilderActionTypes.PARSE_JWT,
  ActionBuilderActionTypes.SET_CONTEXT_VARIABLE,
  ActionBuilderActionTypes.FORETHOUGHT_LIVE_CHAT_HANDOFF,
  ActionBuilderActionTypes.SUNCO_LIVE_CHAT,
  ActionBuilderActionTypes.DYNAMIC_CARD,
  ActionBuilderActionTypes.PRODUCT_RECOMMENDATION,
  ActionBuilderActionTypes.INTERCOM_TICKET_CREATION,
  ActionBuilderActionTypes.INTERCOM_LIVE_CHAT,
  ActionBuilderActionTypes.CONVERSATION_OAUTH_SIGN_IN,
  ActionBuilderActionTypes.ZENDESK_TALK_HANDOFF,
  ActionBuilderActionTypes.ZENDESK_SIP_CALL,
  ActionBuilderActionTypes.GLADLY_CHAT_HANDOFF,
  ActionBuilderActionTypes.CUSTOM_EVENT,
  ActionBuilderActionTypes.GRANULAR_JIRA_SERVICE_MANAGEMENT_CREATE_REQUEST,
  ActionBuilderActionTypes.HUBSPOT_TICKET_CREATION,
  ActionBuilderActionTypes.HUBSPOT_HELPDESK_TICKET_CREATION,
  ActionBuilderActionTypes.HUBSPOT_CHAT_HANDOFF,
  ActionBuilderActionTypes.DYNAMICS_365_CASE_CREATION,
  ActionBuilderActionTypes.COMM_100_CHAT_HANDOFF,
  ActionBuilderActionTypes.QUIQ_CHAT_HANDOFF,
  ActionBuilderActionTypes.CALLCORP_CHAT_HANDOFF,
  ActionBuilderActionTypes.SERVICENOW_CASE_CREATION,
  ActionBuilderActionTypes.SERVICENOW_CASE_INCIDENT_CREATION,
];
